import { useRef, useState } from 'react';
import type { ChangeEvent, PropsWithChildren, FC } from 'react';
import { useIntl } from 'react-intl';

import type { ButtonProps as DefaultButtonProps } from '@xing-com/button';
import { Button as DefaultButton } from '@xing-com/button';

import { checkFileType } from '../../utils';

import * as Styled from './file-upload-button.styles';

type FileUploadButtonProps = Omit<DefaultButtonProps, 'onError'> & {
  Button?: FC;
  onFileSelected: (file: File) => void;
  onError?: (error: string) => void;
  accept: string;
  multiple?: boolean;
};
export const FileUploadButton: FC<PropsWithChildren<FileUploadButtonProps>> = ({
  Button = DefaultButton,
  onError = () => undefined,
  onFileSelected,
  accept,
  multiple = false,
  ...rest
}) => {
  const { $t } = useIntl();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [fileLoading, setFileLoading] = useState(false);

  const handlePreview = (files: File[]): void => {
    const file = files?.[0] ?? undefined;
    setFileLoading(true);

    if (file) {
      const mimeType = file.type;
      const fileName = file.name;

      if (checkFileType(accept, mimeType)) {
        const reader = new FileReader();

        reader.onloadend = () => {
          if (reader.result) {
            const fileBlob = new Blob([reader.result], { type: mimeType });
            const file = new File([fileBlob], fileName, {
              type: fileBlob.type,
            });

            onFileSelected(file);
            setFileLoading(false);
          } else {
            onError($t({ id: 'FILE_READER_ERROR_EMPTY' }));
            setFileLoading(false);
          }
        };

        reader.onerror = () => {
          onError($t({ id: 'FILE_READER_ERROR' }));
          setFileLoading(false);
        };

        reader.readAsArrayBuffer(file);
      } else {
        onError($t({ id: 'INVALID_FILE_TYPE' }));
        setFileLoading(false);
      }
    }
  };

  const handleInputChange = (ev: ChangeEvent<HTMLInputElement>): void => {
    const files = ev.target.files;

    if (files) {
      const fileList = Array.from(files);

      handlePreview(fileList);
    }

    ev.target.value = '';
  };

  const triggerInputFile = (ev: MouseEvent): void => {
    ev.preventDefault();
    fileInputRef.current?.click();
  };

  return (
    <>
      <Styled.InputFile
        type="file"
        tabIndex={-1}
        ref={fileInputRef}
        name="profileImage"
        accept={accept}
        multiple={multiple}
        onChange={handleInputChange}
      />
      {/* @ts-expect-error FIXME: SC6 */}
      <Button onClick={triggerInputFile} loading={fileLoading} {...rest} />
    </>
  );
};
